import React, { useCallback, useState } from 'react';
import { recommendedMinimum } from '../../pages/interview/config/interviewConfigurationModel';

/**
 * Priority Topics editor — shared across every AI-interview creation surface (admin Interview Builder,
 * the scheduler wizard/modal). Add topics (Enter or comma), remove, and Validate them against the job
 * via `onValidate` (relevance check → confirm any off-topic ones with Keep all / Remove those).
 *
 * Produces the SAME structure everywhere: a de-duped array of topic strings written back via onChange,
 * stored on setup_details.priority_topics. Empty → today's behavior (no topic questions).
 *
 * Props:
 *   topics       string[]                      current topics
 *   onChange     (next: string[]) => void      persist the edited list
 *   onValidate   async (topics) => { relevant, irrelevant, advisory }   optional; enables the Validate button
 *   disabled     bool
 */
const splitTopics = (raw) =>
  String(raw || '')
    .split(/[\n,;]+|\s*\d+[.)]\s+/)
    .map((t) => t.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter(Boolean);

const dedupe = (arr) => {
  const seen = new Set();
  const out = [];
  (arr || []).forEach((t) => {
    const k = String(t).trim();
    if (k && !seen.has(k.toLowerCase())) {
      seen.add(k.toLowerCase());
      out.push(k);
    }
  });
  return out;
};

const chipCls =
  'inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
const inputCls =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/40';

export default function PriorityTopicsEditor({ topics = [], onChange, onValidate, disabled = false, example = '' }) {
  const placeholder = example
    ? `Add a topic and press Enter (e.g. ${example})`
    : 'Add a topic and press Enter (e.g. React Hooks, SQL DDL commands)';
  const [input, setInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [confirm, setConfirm] = useState(null); // { relevant, irrelevant }
  const [error, setError] = useState('');
  // Advisory recomputes LIVE from the current topic count (topics are the priority coverage basis), so it
  // never goes stale relative to the chips. The interview is still hard-bounded by the booked duration.
  const advisory = (topics || []).length ? recommendedMinimum((topics || []).length) : null;

  const commit = useCallback(
    (next) => {
      onChange?.(dedupe(next));
    },
    [onChange],
  );

  const addFromInput = useCallback(() => {
    const added = splitTopics(input);
    if (added.length) commit([...(topics || []), ...added]);
    setInput('');
  }, [input, topics, commit]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFromInput();
    }
  };

  const remove = (t) => commit((topics || []).filter((x) => x !== t));

  const runValidate = useCallback(async () => {
    setError('');
    setConfirm(null);
    const list = dedupe(topics);
    if (!list.length || !onValidate) return;
    setValidating(true);
    try {
      const res = (await onValidate(list)) || {};
      const irrelevant = res.irrelevant || [];
      if (irrelevant.length) {
        setConfirm({ relevant: res.relevant || [], irrelevant });
      } else {
        commit(res.relevant && res.relevant.length ? res.relevant : list);
      }
    } catch (err) {
      setError(err?.message || 'Could not validate topics right now.');
    } finally {
      setValidating(false);
    }
  }, [topics, onValidate, commit]);

  return (
    <div className="space-y-2">
      {(topics || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {topics.map((t) => (
            <span key={t} className={chipCls}>
              {t}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(t)}
                  aria-label={`Remove ${t}`}
                  className="ml-0.5 text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <div className="flex gap-2">
          <input
            className={inputCls}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={addFromInput}
            placeholder={placeholder}
          />
          {onValidate && (
            <button
              type="button"
              onClick={runValidate}
              disabled={validating || !(topics || []).length}
              className="shrink-0 rounded-lg border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
            >
              {validating ? 'Validating…' : 'Validate'}
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        The AI asks only these topics for the technical part of the interview. Leave this empty to use the
        job description skills.
      </p>

      {advisory && advisory.minutes ? (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          ⏱️ For these topics plus the usual intro, resume, problem-solving and behavioural questions,
          that&apos;s about <strong>{advisory.questions} main questions</strong> (plus follow-ups) ≈{' '}
          <strong>{advisory.minutes} min</strong>. Consider{' '}
          <strong>at least {advisory.minutes} minutes</strong> — you can still choose less, but some
          lower-priority topics may be dropped when time runs out.
        </p>
      ) : null}

      {error && <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>}

      {confirm && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
          <p className="text-amber-800 dark:text-amber-200">
            These topics don&apos;t appear directly related to this position:
          </p>
          <div className="my-1.5 flex flex-wrap gap-1.5">
            {confirm.irrelevant.map((t) => (
              <span key={t} className={chipCls}>
                {t}
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                commit([...(confirm.relevant || []), ...(confirm.irrelevant || [])]);
                setConfirm(null);
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
            >
              Keep all
            </button>
            <button
              type="button"
              onClick={() => {
                commit(confirm.relevant || []);
                setConfirm(null);
              }}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Remove those
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
